import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { ArrowRight, BookOpen, LifeBuoy, Search } from 'lucide-react';
import { usePermissions } from '@hooks/usePermissions';
import { DOCS, searchDocs, sortDocs } from '@/docs';
import { MENU_ITEMS } from '@/navigation/menu';
import { MarkdownView } from '@components/help/MarkdownView';
import { formatDateBR } from '@/utils/formatters';

/** Ordem dos grupos: a do menu (Hoje, Agenda, Documentos, Acervo CAC, Finanças, Administração) e, por último, "Geral". */
const MENU_ORDER = [...MENU_ITEMS.map((m) => m.label).filter((l) => l !== 'Central de ajuda'), 'Geral'];

/** Central de ajuda: índice de todos os documentos que o usuário pode ver, agrupados por módulo, com busca por título/resumo/corpo. */
export const HelpCenterView: React.FC = () => {
  const { checkPermission } = usePermissions();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState('');

  const visible = useMemo(
    () => sortDocs(DOCS.filter((d) => !d.permissao || checkPermission(d.permissao).hasPermission), MENU_ORDER),
    [checkPermission],
  );
  const results = useMemo(() => searchDocs(visible, query), [visible, query]);
  const selected = visible.find((d) => d.file === params.get('doc')) ?? null;

  const groups = useMemo(() => {
    const out: { menu: string; docs: typeof results }[] = [];
    for (const d of results) {
      let g = out.find((x) => x.menu === d.menu);
      if (!g) {
        g = { menu: d.menu, docs: [] };
        out.push(g);
      }
      g.docs.push(d);
    }
    return out;
  }, [results]);

  const open = (file: string | null) => setParams(file ? { doc: file } : {}, { replace: false });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[20rem_1fr] gap-4 items-start">
      <aside className="card p-3 space-y-3 lg:sticky lg:top-0">
        <h1 className="text-h4 flex items-center gap-2">
          <LifeBuoy size={18} className="text-emerald-600" /> Central de ajuda
        </h1>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="input-field pl-8" placeholder="Buscar na documentação" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <nav className="space-y-3 max-h-[70vh] overflow-y-auto custom-scrollbar">
          {groups.length === 0 && <p className="text-small text-slate-500">Nada encontrado.</p>}
          {groups.map((g) => (
            <div key={g.menu}>
              <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-1">{g.menu}</div>
              {g.docs.map((d) => (
                <button
                  key={d.file}
                  type="button"
                  onClick={() => open(d.file)}
                  className={clsx(
                    'w-full text-left px-2 py-1.5 rounded text-sm',
                    selected?.file === d.file ? 'bg-emerald-100 text-emerald-800' : 'text-slate-700 hover:bg-slate-100',
                  )}
                >
                  {d.titulo}
                </button>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      <section className="card p-5 min-w-0">
        {selected ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2 text-small text-slate-500 mb-2">
              <span>
                {selected.menu} · atualizado em {formatDateBR(selected.atualizado_em)} · versão {selected.versao_funcional}
              </span>
              {selected.tipo === 'tela' && selected.rota !== '/help' && (
                <Link to={selected.rota} className="text-emerald-700 hover:underline inline-flex items-center gap-1">
                  Abrir a tela <ArrowRight size={14} />
                </Link>
              )}
            </div>
            <MarkdownView source={selected.body} />
          </>
        ) : (
          <div className="space-y-4">
            <h2 className="text-h3 flex items-center gap-2">
              <BookOpen size={20} className="text-slate-500" /> Como usar o Plan
            </h2>
            <p className="text-slate-600">
              Cada tela tem um documento com o passo a passo, o significado de cada indicador e o impacto de cada ação. Ele
              também abre pelo botão <strong>?</strong> no topo da própria tela.
            </p>
            {groups.length === 0 && <p className="text-small text-slate-500">Nada encontrado para "{query}".</p>}
            {groups.map((g) => (
              <section key={g.menu} className="space-y-2">
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">{g.menu}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {g.docs.map((d) => (
                    <button key={d.file} type="button" onClick={() => open(d.file)} className="card-hover text-left p-3 rounded-lg border border-slate-200 min-w-0">
                      <div className="font-medium text-slate-800 [overflow-wrap:anywhere]">{d.titulo}</div>
                      <div className="text-small text-slate-500 mt-0.5 [overflow-wrap:anywhere]">{d.resumo}</div>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};
